import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router"
import {
  MessageChatCircle,
  Mail01,
  Phone01,
  ChevronRight,
  SearchLg,
  Announcement01,
} from "@untitledui/icons"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import { useOrganization } from "@/lib/organization/organization-context"
import { useInbox } from "@/lib/api/use-campaigns"
import type { InboxConversation } from "@/lib/api/campaigns"
import { useCustomerActivity } from "@/lib/api/use-customer-activity"
import { CustomerConversation } from "@/components/customers/customer-conversation"
import { PromoDialog } from "@/components/customers/promo-dialog"
import { cx } from "@/lib/utils/cx"

// The communication screen: every conversation the follow-up engine is
// running, "waiting on you" first, with the thread and composer right there —
// someone can sit here, answer customers, and get them booked without
// touching anything else. On mobile a row opens the full Customer Card
// (that IS the conversation surface on a phone).

const STATUS_LABEL: Record<InboxConversation["status"], string> = {
  active: "Following up",
  converted: "Booked",
  unresponsive: "No response",
  stopped: "Paused",
  not_started: "No contact info",
}

// A real opt-out (texted STOP / marked opted-out) is stronger than a pause —
// name it what it legally is.
const statusLabelOf = (r: InboxConversation) =>
  r.status === "stopped" && r.consentState === "opted_out"
    ? "Do not contact"
    : STATUS_LABEL[r.status]

// Slice the inbox by where each conversation stands — same chip idiom as the
// Customers book. Zero-count segments stay hidden.
type InboxSeg = "all" | "waiting" | "active" | "closed" | "paused"
const SEGMENTS: {
  key: InboxSeg
  label: string
  match: (r: InboxConversation) => boolean
}[] = [
  { key: "all", label: "All", match: () => true },
  { key: "waiting", label: "Waiting on you", match: (r) => r.needsReply },
  { key: "active", label: "Following up", match: (r) => r.status === "active" && !r.needsReply },
  { key: "closed", label: "Booked & won", match: (r) => r.status === "converted" },
  { key: "paused", label: "Paused", match: (r) => r.status === "stopped" },
]

// Which channel the message travelled on — the tiny icon answers "did they
// text or email?" without a word.
function ChannelIcon({ channel }: { channel: "sms" | "email" }) {
  const Icon = channel === "email" ? Mail01 : MessageChatCircle
  return <Icon className="size-3 text-zinc-400 shrink-0" aria-hidden />
}

function relTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 60) return "now"
  const h = Math.round(mins / 60)
  if (h < 24) return `${h}h`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function InboxPage() {
  const { activeTeamId } = useOrganization()
  const { data: rows = [], isLoading } = useInbox(activeTeamId)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [seg, setSeg] = useState<InboxSeg>("all")
  const [q, setQ] = useState("")
  const [promoOpen, setPromoOpen] = useState(false)
  const navigate = useNavigate()

  const counts = useMemo(() => {
    const by = {} as Record<InboxSeg, number>
    for (const s of SEGMENTS) by[s.key] = rows.filter(s.match).length
    return by
  }, [rows])

  const filtered = useMemo(() => {
    const match = SEGMENTS.find((s) => s.key === seg)!.match
    const term = q.trim().toLowerCase()
    return rows.filter(
      (r) =>
        match(r) &&
        (!term ||
          r.name.toLowerCase().includes(term) ||
          (r.vehicleLabel ?? "").toLowerCase().includes(term)),
    )
  }, [rows, seg, q])

  // Land on the conversation that needs you most; follow the filter.
  useEffect(() => {
    if (filtered.length === 0) return
    if (selectedId == null || !filtered.some((r) => r.customerId === selectedId)) {
      setSelectedId((filtered.find((r) => r.needsReply) ?? filtered[0]).customerId)
    }
  }, [filtered, selectedId])

  const selected = useMemo(
    () => rows.find((r) => r.customerId === selectedId) ?? null,
    [rows, selectedId],
  )
  const waiting = rows.filter((r) => r.needsReply).length
  const activityQ = useCustomerActivity(selected?.customerId ?? null)

  const openRow = (r: InboxConversation) => {
    // Below lg the detail pane is hidden — the Customer Card is the thread.
    if (window.innerWidth < 1024) navigate(`/customers/${r.customerId}`)
    else setSelectedId(r.customerId)
  }

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          <div className="mx-auto max-w-6xl px-4 md:px-8 py-6 md:py-10">
            <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
                  Inbox
                </h1>
                <p className="text-sm text-zinc-500 mt-0.5">
                  Every conversation your follow-up is running — reply and get them booked.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {waiting > 0 && (
                  <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-amber-50 ring-1 ring-inset ring-amber-200 text-[12.5px] font-semibold text-amber-800">
                    <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
                    {waiting} waiting on you
                  </span>
                )}
                {/* Offer the promotion right where the audience is filtered —
                    "message who you're looking at". */}
                {rows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPromoOpen(true)}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold hover:bg-black transition-colors"
                  >
                    <Announcement01 className="size-4" aria-hidden />
                    Send an offer
                  </button>
                )}
              </div>
            </header>

            {/* Search + state segments — the same slicing idiom as the book. */}
            {activeTeamId != null && rows.length > 0 && (
              <div className="mb-4 space-y-3">
                <div className="relative max-w-md">
                  <SearchLg
                    className="size-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                    aria-hidden
                  />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    aria-label="Search conversations"
                    placeholder="Search name or vehicle…"
                    className="w-full h-10 pl-10 pr-4 rounded-xl border border-zinc-200 bg-white text-[13.5px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300"
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {SEGMENTS.filter((s) => s.key === "all" || counts[s.key] > 0).map((s) => {
                    const active = seg === s.key
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setSeg(s.key)}
                        className={cx(
                          "h-8 px-3 rounded-lg text-[12.5px] font-semibold transition-colors inline-flex items-center gap-1.5",
                          active
                            ? "bg-zinc-900 text-white"
                            : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:text-zinc-900",
                        )}
                      >
                        {s.label}
                        <span className="tabular-nums text-[11px] text-zinc-400">
                          {counts[s.key]}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {activeTeamId == null ? (
              // Group rollup has no single inbox — never show "no conversations"
              // when we simply didn't look.
              <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
                <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
                  <MessageChatCircle className="size-5 text-zinc-400" aria-hidden />
                </div>
                <p className="text-[14px] font-semibold text-zinc-900">
                  Pick a store to open its inbox
                </p>
                <p className="text-[13px] text-zinc-500 mt-1">
                  Conversations belong to a store — choose one from the team switcher.
                </p>
              </div>
            ) : isLoading ? (
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-xl bg-white border border-zinc-200/70" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
                <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
                  <MessageChatCircle className="size-5 text-zinc-400" aria-hidden />
                </div>
                <p className="text-[14px] font-semibold text-zinc-900">No conversations yet</p>
                <p className="text-[13px] text-zinc-500 mt-1">
                  They start on their own — confirm an estimate and the follow-up begins.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)] items-start">
                {/* Conversation list */}
                <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden divide-y divide-zinc-100">
                  {filtered.length === 0 && (
                    <p className="px-4 py-8 text-center text-[12.5px] text-zinc-400">
                      No conversations match.
                    </p>
                  )}
                  {filtered.map((r) => {
                    const active = r.customerId === selectedId
                    return (
                      <button
                        key={r.customerId}
                        type="button"
                        onClick={() => openRow(r)}
                        className={cx(
                          "w-full text-left px-4 py-3 transition-colors",
                          active ? "bg-teal-50/60" : "hover:bg-zinc-50/70",
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {r.needsReply && (
                            <span
                              className="size-2 rounded-full bg-amber-500 shrink-0"
                              aria-label="Waiting on your reply"
                            />
                          )}
                          <p className="text-[13.5px] font-semibold text-zinc-900 truncate flex-1">
                            {r.name}
                          </p>
                          <span className="text-[10.5px] text-zinc-400 tabular-nums shrink-0">
                            {relTime(r.lastActivityAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                          {r.lastMessage && <ChannelIcon channel={r.lastMessage.channel} />}
                          <p className="text-[12px] text-zinc-500 truncate min-w-0">
                            {r.lastMessage ? (
                              <>
                                <span className="text-zinc-400">
                                  {r.lastMessage.direction === "inbound" ? "They: " : "You: "}
                                </span>
                                {r.lastMessage.body}
                              </>
                            ) : (
                              <span className="text-zinc-400">
                                {statusLabelOf(r)} · no reply yet
                              </span>
                            )}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>

                {/* Thread — the work surface (desktop only; mobile opens the card) */}
                <div className="hidden lg:block min-w-0">
                  {selected ? (
                    <div className="space-y-3">
                      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm px-5 py-3.5 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[15px] font-bold text-zinc-900 truncate">
                              {selected.name}
                            </p>
                            <span
                              className={cx(
                                "text-[11px]",
                                selected.status === "converted" && selected.scheduledAt
                                  ? "font-semibold text-sky-700"
                                  : "text-zinc-400",
                              )}
                            >
                              {selected.status === "converted" && selected.scheduledAt
                                ? `Booked · ${new Date(selected.scheduledAt).toLocaleString(
                                    "en-US",
                                    {
                                      weekday: "short",
                                      month: "short",
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                    },
                                  )}`
                                : statusLabelOf(selected)}
                            </span>
                          </div>
                          <p className="text-[12px] text-zinc-400 truncate mt-0.5">
                            {selected.vehicleLabel ?? "Vehicle"}
                            {selected.phone && <> · {selected.phone}</>}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {selected.phone && (
                            <a
                              href={`tel:${selected.phone.replace(/[^\d+]/g, "")}`}
                              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-zinc-600 hover:text-zinc-900"
                            >
                              <Phone01 className="size-3.5" aria-hidden />
                              Call
                            </a>
                          )}
                          <Link
                            to={`/customers/${selected.customerId}`}
                            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-emerald-700 hover:text-emerald-800"
                          >
                            Full customer
                            <ChevronRight className="size-3.5" aria-hidden />
                          </Link>
                        </div>
                      </div>
                      {activityQ.data ? (
                        <CustomerConversation
                          customerId={selected.customerId}
                          events={activityQ.data.events}
                          consent={activityQ.data.consent}
                          customerName={selected.name}
                        />
                      ) : (
                        <div className="rounded-2xl border border-zinc-200/80 bg-white p-6 text-center text-[13px] text-zinc-400">
                          Loading conversation…
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center text-[13px] text-zinc-400">
                      Pick a conversation.
                    </div>
                  )}
                </div>
              </div>
            )}

            <PromoDialog
              open={promoOpen}
              onClose={() => setPromoOpen(false)}
              recipients={filtered.map((r) => ({
                id: r.customerId,
                phone: r.phone,
                email: r.email,
              }))}
              audienceLabel={SEGMENTS.find((s) => s.key === seg)!.label}
            />
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}
